import { useEffect, useId, useRef } from "react";
import { useI18n } from "../i18n";

interface ModelSwitchConfirmDialogProps {
  open: boolean;
  fromLabel: string;
  toLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation dialog that gates client-originated model switches.
 *
 * Reuses the shared .dialog-overlay / .dialog-card CSS so it matches the
 * NewSessionDialog design language exactly. .dialog-card--narrow caps width at 400px.
 *
 * The gate lives in useChat.ts's config_change effect (not in UI components)
 * so every code path that changes chatConfig.model is covered.
 *
 * Accessibility: role="dialog", aria-modal, aria-labelledby, focus trap (Tab/Shift+Tab),
 * focus capture on open, focus restore on close.
 */
export function ModelSwitchConfirmDialog({
  open,
  fromLabel,
  toLabel,
  onConfirm,
  onCancel,
}: ModelSwitchConfirmDialogProps) {
  const { t } = useI18n();
  const titleId = useId();
  const bodyId = useId();
  const overlayRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);

  // Focus management: capture trigger element before focus moves, focus dialog on
  // open; restore focus to the trigger when the dialog closes.
  useEffect(() => {
    if (!open) return;
    triggerRef.current = document.activeElement;
    cardRef.current?.focus();
    return () => {
      (triggerRef.current as HTMLElement | null)?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onCancel();
        return;
      }
      // Focus trap: cycle Tab/Shift+Tab between the two action buttons.
      if (e.key === "Tab" && cardRef.current) {
        const focusable = Array.from(
          cardRef.current.querySelectorAll<HTMLElement>("button:not([disabled])")
        );
        if (focusable.length === 0) return;
        e.preventDefault();
        const activeIndex = focusable.indexOf(document.activeElement as HTMLElement);
        if (e.shiftKey) {
          const prev = activeIndex <= 0 ? focusable.length - 1 : activeIndex - 1;
          focusable[prev].focus();
        } else {
          const next = activeIndex >= focusable.length - 1 ? 0 : activeIndex + 1;
          focusable[next].focus();
        }
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="dialog-overlay"
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onCancel(); }}
    >
      <div
        className="dialog-card dialog-card--narrow"
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex={-1}
      >
        <div className="dialog-title" id={titleId}>
          {t("modelSwitch.confirm.title")}
        </div>
        <div className="dialog-subtitle" id={bodyId}>
          {t("modelSwitch.confirm.body", { from: fromLabel, to: toLabel })}
        </div>
        <div className="dialog-actions">
          <button className="btn btn-primary" onClick={onConfirm} autoFocus>
            {t("modelSwitch.confirm.switch")}
          </button>
          <button className="btn btn-ghost" onClick={onCancel}>
            {t("sessionDialog.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}
